import { userService } from "@services/user";
import { useCallback, useMemo, useState } from "react";
import type { UserSettingsInput } from "~/api/API";
import { useCurrentUser } from "~/auth/UserContext";

/**
 * Read and write the current user's portal settings (the User settings modal).
 *
 * These live on `User.settings` rather than in localStorage — unlike theme and
 * font size, which describe one browser. A workflow default should follow the
 * user across machines, and should not be inherited from whoever last used a
 * shared workstation.
 */
export const useUserSettings = () => {
  const { user, refreshUser } = useCurrentUser();
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Current settings as a mutation input: `__typename` comes back on the
   * queried object but AppSync rejects it inside an input, so it is dropped
   * here rather than at each callsite.
   */
  const settings = useMemo<UserSettingsInput>(
    () => ({
      opsChecklistDefaultExpanded:
        user?.settings?.opsChecklistDefaultExpanded ?? null,
    }),
    [user?.settings?.opsChecklistDefaultExpanded],
  );

  /**
   * Merge `patch` into the stored settings and persist. The mutation replaces
   * the whole object, so the merge is what keeps the untouched fields.
   * Resolves to whether the write landed; the caller decides what a failure
   * looks like.
   */
  const updateSettings = useCallback(
    async (patch: UserSettingsInput): Promise<boolean> => {
      const userId = user?.id;

      if (!userId) {
        return false;
      }

      setIsSaving(true);

      try {
        await userService.updateSettings(userId, { ...settings, ...patch });
        await refreshUser?.();
        return true;
      } catch (error) {
        console.warn("Failed to persist user settings", error);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [refreshUser, settings, user?.id],
  );

  return { settings, updateSettings, isSaving };
};
